import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { paymentService } from '../services/paymentService';

export default function PaymentApproval() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    if (user) {
      loadPendingApprovals();
    }
  }, [user]);

  const loadPendingApprovals = async () => {
    setLoading(true);
    try {
      console.log('Loading pending approvals for user:', user.id);
      const result = await paymentService.getPendingApprovals(user.id);
      console.log('Pending approvals result:', result);
      if (result.success) {
        console.log('Pending approvals data:', result.data);
        setPendingApprovals(result.data);
      } else {
        console.error('Failed to load pending approvals:', result.message);
      }
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (transactionId) => {
    setProcessing(transactionId);
    try {
      const result = await paymentService.approvePayment(transactionId, user.id);
      if (result.success) {
        await loadPendingApprovals(); // Refresh the list
      }
    } catch (error) {
      console.error('Error approving payment:', error);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (transactionId) => {
    const reason = prompt(t('payment.rejectionReason', 'Please provide a reason for rejection:'));
    if (!reason) return;

    setProcessing(transactionId);
    try {
      const result = await paymentService.rejectPayment(transactionId, reason, user.id);
      if (result.success) {
        await loadPendingApprovals(); // Refresh the list
      }
    } catch (error) {
      console.error('Error rejecting payment:', error);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-slate-600">{t('common.loading')}</p>
      </div>
    );
  }

  if (pendingApprovals.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {t('payment.noPendingApprovals', 'No Pending Approvals')}
        </h3>
        <p className="text-slate-600">
          {t('payment.noPendingApprovalsDescription', 'All payment requests have been reviewed.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900">
          {t('payment.pendingApprovals', 'Pending Payment Approvals')}
        </h2>
        <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
          {pendingApprovals.length} {t('payment.pending', 'pending')}
        </span>
      </div>

      {pendingApprovals.map((transaction) => (
        <div key={transaction.id} className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src={transaction.listings?.photos?.[0] || '/placeholder.jpg'}
                  alt={transaction.listings?.title}
                  className="w-12 h-12 rounded-lg object-cover"
                />
                <div>
                  <h3 className="font-semibold text-slate-900">
                    {transaction.listings?.title}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {t('payment.from', 'Payment from')} {transaction.buyer?.name}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    {t('payment.amount')}
                  </label>
                  <p className="text-lg font-bold text-green-600">
                    {paymentService.formatAmount(transaction.amount_cents)}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    {t('payment.method')}
                  </label>
                  <p className="text-sm text-slate-700">
                    {transaction.payment_method}
                  </p>
                </div>
              </div>

              {transaction.notes && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    {t('payment.notes')}
                  </label>
                  <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">
                    {transaction.notes}
                  </p>
                </div>
              )}

              <div className="text-xs text-slate-500">
                {t('payment.requestedAt', 'Requested')}: {new Date(transaction.created_at).toLocaleString()}
              </div>
            </div>

            <div className="flex flex-col gap-2 ml-4">
              <button
                onClick={() => handleApprove(transaction.id)}
                disabled={processing === transaction.id}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
              >
                {processing === transaction.id ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {t('payment.approve', 'Approve')}
              </button>

              <button
                onClick={() => handleReject(transaction.id)}
                disabled={processing === transaction.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
              >
                {processing === transaction.id ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {t('payment.reject', 'Reject')}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
